import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw, Save, Sparkles } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { claudeQueryService, type SavedReportDTO, type WidgetReportDataDTO } from '../../../../../services/claudeQueryService';
import { ViewRenderer } from '../view/ViewRenderer';
import { ViewAdjustBar } from '../view/ViewAdjustBar';
import { useViewHistory } from '../view/useViewHistory';
import { chartConfigForSave, viewFromLegacyReport } from '../view/legacy';
import { viewFromChartConfig, type DatasetMap, type ViewSpec } from '../view/types';

interface ReportViewerProps {
    report: SavedReportDTO;
    onBack: () => void;
    /** Rapporten ble endret (visning lagret) — listen bør lastes på nytt */
    onChanged?: () => void;
}

/**
 * Viser en lagret rapport med dens Compass-visning. Dataene hentes på nytt fra databasen hver gang
 * (widget-data, ingen tokens). Eldre rapporter uten visning vises som graf/tabell, og «Lag visning med
 * Compass» lager en visning (ett Claude-kall, kun ved klikk).
 */
export const ReportViewer: React.FC<ReportViewerProps> = ({ report, onBack, onChanged }) => {
    const { t } = useTranslation();
    const [data, setData] = useState<WidgetReportDataDTO | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [designing, setDesigning] = useState(false);
    const [saving, setSaving] = useState(false);
    const [note, setNote] = useState<string | null>(null);
    const [chartConfig, setChartConfig] = useState<string | undefined>(report.chartConfig);
    const [exportFormat, setExportFormat] = useState<string>(report.exportFormat);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await claudeQueryService.getWidgetData(report.reportId));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
        }
    }, [report.reportId]);

    useEffect(() => { load(); }, [load]);

    const rows: Record<string, any>[] = useMemo(() => {
        try { return data?.resultSnapshot ? JSON.parse(data.resultSnapshot) : []; } catch { return []; }
    }, [data?.resultSnapshot]);

    const savedView = useMemo(() => viewFromChartConfig(chartConfig), [chartConfig]);
    const columns = useMemo(() => data?.columns ?? (rows[0]
        ? Object.keys(rows[0]).map(name => ({ name, displayName: name, dataType: typeof rows[0][name] === 'number' ? 'number' : 'string', sortable: true, filterable: true } as any))
        : []), [data?.columns, rows]);

    const initialView: ViewSpec | undefined = useMemo(() => {
        if (!data) return undefined;
        return savedView ?? viewFromLegacyReport(report.name, chartConfig, exportFormat, columns);
    }, [data, savedView, report.name, chartConfig, exportFormat, columns]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-500">
                <Loader2 className="animate-spin text-blue-500 mr-2" size={24} />
                {t('manager.modules.claudeQuery.view.loadingReport', {}, 'Henter rapportdata…')}
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                <button onClick={onBack} className="flex items-center gap-1 min-h-[44px] px-3 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
                    <ArrowLeft size={16} /> {t('common.buttons.back', {}, 'Tilbake')}
                </button>
                <h3 className="text-base font-semibold text-gray-800 flex-1 truncate">{report.name}</h3>
                <button onClick={load} disabled={loading} className="flex items-center gap-1 min-h-[44px] px-3 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                    <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> {t('manager.modules.claudeQuery.view.refresh', {}, 'Oppdater data')}
                </button>
                <button
                    onClick={async () => {
                        setDesigning(true);
                        setNote(null);
                        try {
                            const result = await claudeQueryService.designReportView(report.reportId);
                            if (result.success && result.chartConfig) {
                                setChartConfig(result.chartConfig);
                                setExportFormat(result.exportFormat ?? exportFormat);
                                setNote(result.message);
                                onChanged?.();
                                await load();
                            } else {
                                setNote(result.errorMessage ?? t('manager.modules.claudeQuery.view.adjustFailed', {}, 'Compass klarte ikke å lage visningen.'));
                            }
                        } catch (err) {
                            setNote(err instanceof Error ? err.message : String(err));
                        } finally {
                            setDesigning(false);
                        }
                    }}
                    disabled={designing || !data?.success}
                    className="flex items-center gap-1 min-h-[44px] px-3 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50"
                >
                    {designing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {savedView
                        ? t('manager.modules.claudeQuery.view.redesign', {}, 'Lag visningen på nytt')
                        : t('manager.modules.claudeQuery.view.design', {}, 'Lag visning med Compass')}
                </button>
            </div>
            <p className="text-xs text-gray-400 mb-2">
                {savedView
                    ? t('manager.modules.claudeQuery.view.savedViewHint', {}, 'Visningen er lagret på rapporten. Dataene hentes på nytt hver gang, uten Compass.')
                    : t('manager.modules.claudeQuery.view.legacyHint', {}, 'Rapporten har ingen Compass-visning ennå. «Lag visning med Compass» lager en (bruker noen tokens).')}
            </p>
            {note && <p className="text-sm text-gray-700 mb-2">{note}</p>}

            {error || data?.success === false ? (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">{error ?? data?.errorMessage}</div>
            ) : initialView && data ? (
                <ReportViewBody
                    key={chartConfig ?? 'legacy'}
                    initialView={initialView}
                    reportId={report.reportId}
                    primarySql={report.generatedSQL}
                    data={data}
                    rows={rows}
                    columns={columns}
                    saving={saving}
                    onSave={async (view) => {
                        setSaving(true);
                        try {
                            const { chartConfig: next, exportFormat: format } = chartConfigForSave(view);
                            await claudeQueryService.updateReport(report.reportId, { chartConfig: next, exportFormat: format });
                            setChartConfig(next);
                            setExportFormat(format);
                            setNote(t('manager.modules.claudeQuery.view.saved', {}, 'Visningen er lagret.'));
                            onChanged?.();
                        } catch (err) {
                            setNote(err instanceof Error ? err.message : String(err));
                        } finally {
                            setSaving(false);
                        }
                    }}
                />
            ) : null}
        </div>
    );
};

const ReportViewBody: React.FC<{
    initialView: ViewSpec;
    reportId: number;
    primarySql: string;
    data: WidgetReportDataDTO;
    rows: Record<string, any>[];
    columns: any[];
    saving: boolean;
    onSave: (view: ViewSpec) => void;
}> = ({ initialView, reportId, primarySql, data, rows, columns, saving, onSave }) => {
    const { t } = useTranslation();
    const history = useViewHistory(initialView);
    const view = history.view ?? initialView;

    const [extra, setExtra] = useState<DatasetMap>({});
    const datasets: DatasetMap = useMemo(() => {
        const primaryId = view.datasets[0]?.id ?? 'main';
        return {
            [primaryId]: { id: primaryId, sql: primarySql, columns, rows, rowCount: rows.length },
            ...((data.datasets ?? {}) as DatasetMap),
            ...extra
        };
    }, [view.datasets, primarySql, columns, rows, data.datasets, extra]);

    const datasetSql = useMemo(() => Object.fromEntries(view.datasets.map((d, i) =>
        [d.id, i === 0 ? primarySql : (d.sql ?? datasets[d.id]?.sql ?? '')]).filter(([, sql]) => !!sql)), [view.datasets, primarySql, datasets]);

    return (
        <div className="flex-1 min-h-0 overflow-auto">
            <ViewRenderer view={view} datasets={datasets} onViewChange={history.push} />
            <ViewAdjustBar
                view={view}
                datasets={datasets}
                datasetSql={datasetSql}
                reportId={reportId}
                onAdjusted={(v, ds) => { history.push(v); if (ds) setExtra(prev => ({ ...prev, ...ds })); }}
                canUndo={history.canUndo}
                canRedo={history.canRedo}
                isModified={history.isModified}
                onUndo={history.undo}
                onRedo={history.redo}
                onReset={history.reset}
            />
            {history.isModified && (
                <div className="mt-2 flex justify-end">
                    <button
                        onClick={() => onSave(view)}
                        disabled={saving}
                        className="flex items-center gap-1 min-h-[44px] px-4 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                        {t('manager.modules.claudeQuery.view.saveView', {}, 'Lagre visningen på rapporten')}
                    </button>
                </div>
            )}
        </div>
    );
};
